import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core'
import { Router } from '@angular/router'
import Mark from 'mark.js'
import { map } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'

import {
  ContentRecord,
  Hunt,
  HuntPackageRecord,
  HuntPackagesAdded,
  HuntTemplate,
} from '@cyborg-monorepo/hunter/common'
import { DialogRef, DialogService, SnackbarService } from '@hyperkit'

import { AuthService } from '../../../../auth/auth.service'
import { FeatureFlagService } from '../../../../helpers/feature-flag.service'
import {
  gatherTags,
  isHuntPackageHuntCompatible,
} from '../../../../helpers/utils'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service'
import { SearchStateService } from '../../../research/services/search-state.service'
import { HuntPackageService, UserService } from '../../services'
import { CalendlyService } from '../../services/calendly.service'
import { FavoriteService } from '../../services/favorite.service'
import { CyborgContentComponent } from '../cyborg-content/cyborg-content.component'
import { AddToQuickHuntDialogComponent } from '../dialogs/add-to-quick-hunt-dialog/add-to-quick-hunt-dialog.component'
import { AddToTemplateDialogComponent } from '../dialogs/add-to-template-dialog/add-to-template-dialog.component'
import { HuntModuleDialogComponent } from '../dialogs/hunt-module-dialog/hunt-module-dialog.component'
import { NewHuntDialogComponent } from '../dialogs/new-hunt-dialog/new-hunt-dialog.component'
import { NewTemplateDialogComponent } from '../dialogs/new-template-dialog/new-template-dialog.component'
import { FacetButtonItem } from '../facet-buttons/facet-buttons.component'

@Component({
  standalone: false,
  selector: 'hunt-package',
  templateUrl: './hunt-package.component.html',
  styleUrls: ['./hunt-package.component.scss'],
})
export class HuntPackageComponent implements OnInit, AfterViewInit {
  private myElement = inject(ElementRef)
  private searchStateService = inject(SearchStateService)
  private userService = inject(UserService)
  private favoriteService = inject(FavoriteService)
  private dialogService = inject(DialogService)
  router = inject(Router)
  private featureFlagService = inject(FeatureFlagService)
  calendlyService = inject(CalendlyService)
  trackingService = inject(TrackingService)
  private huntPackageService = inject(HuntPackageService)

  @ViewChild(CyborgContentComponent)
  public cyborgContent: CyborgContentComponent
  public locked$: Observable<boolean>
  @Input() public data: HuntPackageRecord
  public contents: Array<ContentRecord>
  public favorited: boolean
  public tags: FacetButtonItem[]
  public huntCompatible = false
  public huntModuleAccess = false
  public readonly snack = inject(SnackbarService)

  public ngOnInit(): void {
    this.locked$ = this.userService.profile$.pipe(
      map((profile) => {
        return !this.huntPackageService.huntPackageIsAccessibleForUser(
          this.data,
          profile,
        )
      }),
    )

    this.isFavorited()
    this.isHuntCompatible()
    this.huntModuleAccess = this.featureFlagService.huntModuleAccess()
    this.tags = gatherTags(this.data)
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.textHightlighting()
    }, 0)
  }

  private isFavorited(): void {
    this.favoriteService
      .checkThreatProfileFavorite(this.data.UUID)
      .subscribe((favorite: boolean) => {
        this.favorited = favorite
      })
  }

  private isHuntCompatible(): void {
    this.huntCompatible = isHuntPackageHuntCompatible(this.data)
  }

  public openHuntDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_OPENED,
    )
    const dialogRef: DialogRef = this.dialogService.open(
      HuntModuleDialogComponent,
      {
        panelClass: 'full-panel',
      },
    )

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg()
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_ABANDONED,
        )
      }
    })
  }

  public talkToCyborg(): void {
    this.calendlyService.talkToCyborg()
  }

  public favorite(): void {
    this.favoriteService.favoriteHuntPackage(this.data.UUID).subscribe(() => {
      this.favorited = true
      this.favoriteService.setUpdated(true)
    })
  }

  public unfavorite(): void {
    this.favoriteService.unfavoriteHuntPackage(this.data.UUID).subscribe(() => {
      this.favorited = false
      this.favoriteService.setUpdated(true)
    })
  }

  public onOpen(target: HTMLElement): void {
    const huntPackage =
      target.parentElement.parentElement.parentElement.parentElement
        .parentElement
    huntPackage.classList.toggle('is-opened')
  }

  public addToExistingQuickHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      AddToQuickHuntDialogComponent,
      { data: { huntPackages: [this.data] } },
    )

    dialogRef.afterClosed().subscribe((huntMeta: HuntPackagesAdded) => {
      if (huntMeta)
        this.snack.show(
          `${huntMeta.huntPackagesAdded} new Hunt Package${huntMeta.huntPackagesAdded !== 1 ? 's' : ''} added to Hunt:`,
          5000,
          {
            text: huntMeta.name,
            callback: () => this.router.navigate(['hunt', huntMeta.id]),
          },
        )
    })
  }

  public addToNewQuickHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewHuntDialogComponent,
      { data: { huntPackages: [this.data] } },
    )
    dialogRef.afterClosed().subscribe((hunt: Hunt) => {
      if (hunt) {
        this.router.navigate(['hunt', hunt.id, 'workbench'])
      }
    })
  }

  public addToExistingTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      AddToTemplateDialogComponent,
      { data: { huntPackages: [this.data] } },
    )

    dialogRef.afterClosed().subscribe((templateMeta: HuntPackagesAdded) => {
      if (templateMeta)
        this.snack.show(
          `${templateMeta.huntPackagesAdded} new Hunt Package${templateMeta.huntPackagesAdded !== 1 ? 's' : ''} added to Hunt Template:`,
          5000,
          {
            text: templateMeta.name,
            callback: () =>
              this.router.navigate(['hunt', 'hunt-template', templateMeta.id]),
          },
        )
    })
  }

  public addToNewTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewTemplateDialogComponent,
      { data: { huntPackages: [this.data] } },
    )
    dialogRef.afterClosed().subscribe((result: HuntTemplate) => {
      if (result) this.router.navigate(['hunt', 'hunt-template', result.id])
    })
  }

  private textHightlighting(): void {
    const context = this.myElement.nativeElement.querySelectorAll(
      'package-description, header-title',
    )
    const instance = new Mark(context)
    const term = this.searchStateService.state.value.term
    if (term) {
      instance.mark(term, {
        element: 'em',
        className: 'highlight',
      })
    }
  }
}
